import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { useDebounce } from '$hooks/useDebounce';
import {
  SearchCandidateOutput,
  useAddMultipleCandidateToPositionMutation,
} from '$store/modules/candidates';
import { useLazyLoadOnePositionQuery } from '$store/modules/companies';
import { useGetAllTenantsQuery } from '$store/modules/tenant';
import { errorHandler, successHandler } from '$store/utils';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';

const SearchCandidateAddToPositionModal = ({
  isOpen,
  onCloseHandler,
  searchCandidateData,
  selectedCandidates,
  setSelectedCandidates,
}: {
  isOpen: boolean;
  onCloseHandler: () => void;
  searchCandidateData: SearchCandidateOutput['data'];
  selectedCandidates: any;
  setSelectedCandidates: (prev: any) => void;
}) => {
  const [positionId, setPositionId] = useState<null | number>(null);
  const [showPositionData, setShowPositionData] = useState(false);
  const [positionData, setPositionData] = useState<any>(null);
  const [loadOnePosition, { isLoading, isFetching, isError }] =
    useLazyLoadOnePositionQuery();
  const { data: tenantsData } = useGetAllTenantsQuery();
  const [addMultipleCandidateToPosition] =
    useAddMultipleCandidateToPositionMutation();

  const positionIdDebouncedValue = useDebounce(positionId, 1000);

  const selectedCandidatesData: SearchCandidateOutput['data'] = useMemo(() => {
    return searchCandidateData.reduce((prev: any, act: any) => {
      if (selectedCandidates[act.Id]) prev.push(act);
      return prev;
    }, []);
  }, [searchCandidateData, selectedCandidates]);

  const closeHandler = () => {
    setPositionId(null);
    setPositionData(false);
    setShowPositionData(false);
    onCloseHandler();
  };

  const submitHandler = async () => {
    if (positionId) {
      await addMultipleCandidateToPosition({
        candidateIds: Object.entries(selectedCandidates).reduce(
          (prev: any, [key, value]: any) => {
            if (value) prev.push(+key);
            return prev;
          },
          []
        ),
        positionId,
      })
        .then(() => {
          successHandler('Candidates added successfully', true, false);
          closeHandler();
        })
        .catch((err) => {
          errorHandler(
            '[AddMultipleCandidateToPosition]',
            'An error ocurred. Try again later',
            err,
            true,
            false
          );
        });
    }
  };

  const removeCandidateFromSelection = (candidateId: number) => {
    setSelectedCandidates((prev: any) => {
      const temp = { ...prev };
      temp[candidateId] = false;
      return temp;
    });
  };

  useEffect(() => {
    if (positionIdDebouncedValue && positionIdDebouncedValue > 0) {
      (async () => {
        await loadOnePosition({ id: positionIdDebouncedValue }).then((resp) =>
          setPositionData(resp.data)
        );
      })();
    }
  }, [loadOnePosition, positionIdDebouncedValue]);

  return (
    <ModalInTailwind isOpen={isOpen} closeModal={closeHandler}>
      {/* <div className="h-screen max-h-screen w-screen max-w-full items-center flex justify-center pointer-events-auto"> */}
      <div className="bg-white rounded p-4 md:p-8 w-full max-w-3xl relative">
        <button
          type="button"
          className="absolute top-2 right-2"
          aria-label="Close"
          onClick={closeHandler}
        >
          <i className="pi pi-times" />
        </button>
        <div className="flex flex-col relative mb-4 gap-2 ">
          <div>
            <h1 className="font-semibold text-xl">Position ID</h1>
            <div className="p-inputgroup flex-1 relative flex flex-row items-center justify-center">
              <InputNumber
                placeholder="Position Id"
                value={positionId}
                onValueChange={(e) => {
                  if (e.value) setPositionId(e.value);
                }}
                onChange={(e) => {
                  if (e.value) setPositionId(e.value);
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    setPositionId(
                      e?.currentTarget?.value ? +e.currentTarget.value : null
                    );
                  }
                }}
                disabled={isLoading || isFetching}
              />
              {isLoading ||
                (isFetching && (
                  <div className="absolute right-4 top-0 bottom-0 flex items-center">
                    <i className="pi pi-spinner pi-spin text-xl opacity-80 text-internal-main" />
                  </div>
                ))}
            </div>
          </div>
          <div>
            <button
              type="button"
              className="font-semibold text-sm"
              onClick={() => setShowPositionData((prev) => !prev)}
            >
              Position Details{' '}
              <i
                className={`pi pi-sort-${
                  showPositionData ? 'up' : 'down'
                }-fill`}
              />
            </button>
          </div>
          <div
            className={`flex flex-col gap-4 relative ${
              showPositionData ? 'block' : 'hidden'
            }`}
          >
            {isLoading && (
              <div className="flex flex-col gap-2 absolute mx-auto top-0 bottom-0 bg-white/90 w-full text-center">
                <div className="relative self-center">
                  <i className="pi pi-spinner animate-spin text-3xl" />
                </div>
                <span>Loading information...</span>
              </div>
            )}
            <div className="flex flex-row gap-4">
              <div>
                <label htmlFor="positionName" className="flex text-xs">
                  Position Name
                </label>
                <InputText
                  className={`${
                    !positionId || isError ? 'hover:cursor-not-allowed' : ''
                  } caret-transparent`}
                  readOnly
                  placeholder={
                    isError
                      ? 'Position Id not found'
                      : 'Position ID not provided'
                  }
                  value={
                    positionData?.CustomJobTitle ||
                    positionData?.name ||
                    positionData?.JobPosition?.title ||
                    ''
                  }
                  id="positionName"
                />
              </div>
              <div>
                <label htmlFor="companyName" className="flex text-xs">
                  Company
                </label>
                <InputText
                  className={`${
                    !positionId || isError ? 'hover:cursor-not-allowed' : ''
                  } caret-transparent`}
                  id="companyName"
                  readOnly
                  placeholder={
                    isError
                      ? 'Position Id not found'
                      : 'Position ID not provided'
                  }
                  value={positionData?.Company?.Name || ''}
                />
              </div>
              <div>
                <label htmlFor="tenant" className="flex text-xs">
                  Tenant
                </label>
                <InputText
                  className={`${
                    !positionId || isError ? 'hover:cursor-not-allowed' : ''
                  } caret-transparent`}
                  placeholder={
                    isError
                      ? 'Position Id not found'
                      : 'Position ID not provided'
                  }
                  id="tenant"
                  readOnly
                  value={
                    tenantsData?.find(
                      (singleTenant) =>
                        singleTenant.Id === positionData?.TenantId
                    )?.title || ''
                  }
                />
              </div>
            </div>
          </div>

          <hr className="border-b-0 border-solid border-internal-main opacity-25" />
          <div>
            <h1 className="font-semibold text-xl mb-2">
              Selected Candidates (Total: {selectedCandidatesData?.length})
            </h1>
            <div>
              {selectedCandidatesData && selectedCandidatesData.length > 0 && (
                <ol className="list-decimal list-inside [&>li]:marker:text-base [&>li]:marker:font-bold flex flex-col gap-2 max-h-96 overflow-scroll">
                  {selectedCandidatesData.map((singleCandidate) => (
                    <li
                      key={`searchAddToPosition_candidate${singleCandidate.Id}`}
                      className="relative ml-8"
                    >
                      <span>
                        {singleCandidate.User.FirstName || ''}{' '}
                        {singleCandidate.User.LastName || ''}
                        <span className="ml-2 text-xs inline-block">
                          (ID: {singleCandidate.Id})
                        </span>
                      </span>
                      <button
                        type="button"
                        aria-label="Remove"
                        className="absolute -left-8"
                        onClick={() =>
                          removeCandidateFromSelection(singleCandidate.Id)
                        }
                      >
                        <i className="pi pi-trash" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4 items-end justify-end">
          <button
            type="button"
            onClick={closeHandler}
            className="bg-red-400 px-2 py-1 font-semibold text-white rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded px-2 py-1 text-white font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed bg-internal-main"
            disabled={!positionId || !positionData}
            onClick={submitHandler}
          >
            Submit
          </button>
        </div>
      </div>
      {/* </div> */}
    </ModalInTailwind>
  );
};

export default SearchCandidateAddToPositionModal;
